import { TranslationFunction } from 'i18next';
import { EditorSDK, EditorPlatformApp } from '@wix/platform-editor-sdk';
import { MEMBERS_AREA } from '@wix/app-definition-ids';
import {
  MA_APP_IDS,
  MembersAreaAppId,
} from '@wix/members-area-app-definitions';

import { EditorReadyOptions, Experiments } from './types';
import { manifestFactory } from './manifest';
import { handleOrFallback } from './error-handlers';
import { getTranslationFunction } from './i18n';
import {
  shouldRegisterGfppEventListeners,
  registerGfppEventListeners,
} from './event-listeners';
import * as api from './api';

interface SantaMembersAppApi {
  refreshMembersAreaApps(): Promise<void>;
  addApplications(definitions: MembersAreaAppId[]): Promise<void>;
}

let appDefinitionId: string;
let editorSDK: EditorSDK;
let editorOptions: EditorReadyOptions;
let t: TranslationFunction | null = null;
let experiments: Experiments | null = null;

const getSantaMembersApi = (_editorSDK: EditorSDK) => {
  const payload = { appDefinitionId: MEMBERS_AREA };
  const santaMembersApi = _editorSDK.application.getPublicAPI('', payload);

  return santaMembersApi as Promise<SantaMembersAppApi>;
};

const getAppApi = (_editorSDK: EditorSDK) => ({
  refreshMembersAreaApps: async () => {
    const membersEditorApi = await getSantaMembersApi(_editorSDK);
    return membersEditorApi.refreshMembersAreaApps();
  },
  installFollowersLightbox: async () => {
    const membersEditorApi = await getSantaMembersApi(_editorSDK);
    return membersEditorApi.addApplications([MA_APP_IDS.FOLLOWERS_LIGHTBOX]);
  },
  openMembersAccountBm: (origin: string) => {
    return _editorSDK.editor.openDashboardPanel('', {
      url: `/members-account/?referralInfo=${origin}`,
      closeOtherPanels: false,
    });
  },
  openBadgesBm: (origin: string) => {
    return _editorSDK.editor.openDashboardPanel('', {
      url: `/badge-definitions/?referralInfo=${origin}`,
      closeOtherPanels: false,
    });
  },
});

const initTranslationsAndExperiments = (_editorSDK: EditorSDK) => async () => {
  experiments = await editorOptions.essentials.createExperiments(
    'members-area',
  );

  await Promise.all([
    getTranslationFunction(_editorSDK).then((_t) => (t = _t)),
  ]);
};

const editorReady: EditorPlatformApp['editorReady'] = async (
  _editorSDK,
  appDefId,
  options,
) => {
  appDefinitionId = appDefId;
  editorSDK = _editorSDK;

  // eslint-disable-next-line prettier/prettier
  editorOptions = options as unknown as EditorReadyOptions;

  await editorSDK.editor.setAppAPI('', getAppApi(editorSDK));
  await handleOrFallback(initTranslationsAndExperiments(editorSDK), undefined);

  if (shouldRegisterGfppEventListeners(appDefinitionId)) {
    registerGfppEventListeners(editorSDK);
  }
};

const getAppManifest: EditorPlatformApp['getAppManifest'] = () => {
  if (t) {
    return handleOrFallback(() => manifestFactory(appDefinitionId, t!), {});
  }

  return {};
};

const exports_ = {
  refreshApp: () => api.refreshApp(editorSDK),
  setBooleanStyleParamForWidgets: (key: string, value: boolean) => {
    return api.setBooleanStyleParamForWidgets(
      editorSDK,
      appDefinitionId,
      key,
      value,
    );
  },
};

export { editorReady, getAppManifest, exports_ as exports };
